// src/oura/docs/hooks.ts
//
// The documents reads, as hooks. Each returns `{status, data, error,
// loadedAt, reload}`:
//   - "loading": the first read has not come back. Show placeholders, never 0.
//   - "ready":   `data` is the last good read. A refresh that failed after it
//                keeps `data` and sets `error`, so the rows stay with a banner.
//   - "error":   the first read failed and there is nothing to show. It
//                retries every 15 seconds ("not served yet" every 60).
// They refetch on focus, every 60 seconds while the tab is visible, and
// straight after any documents write (Pick a code, reply-by, link, read now).

import { useCallback, useEffect, useRef, useState } from "react";
import {
    getDocsAcrossClients,
    getDocsConnection,
    getDocsFolder,
    getDocument,
    isDocsLive,
    listBusinessDocuments,
    subscribeDocs,
    type BusinessDocuments,
    type DocsAcrossClients,
    type DocsConnection,
    type DocsFailure,
    type DocsFolder,
    type DocsResult,
    type DocumentDetail,
} from "@service/aeo/clientDocs";
import { readChangeRequests, type ChangeRequest } from "@service/aeo/clientReview";
import { useVisiblePoll } from "../lib/useVisiblePoll";
import { BOARD_RUN_POLL_MS, boardRunGoing } from "./skillRun";

export type DocsLoadStatus = "loading" | "ready" | "error";

export interface DocsLoad<T> {
    status: DocsLoadStatus;
    /** The last good read; kept through a failed refresh. */
    data: T | null;
    /** The last failure. With status "ready" it means a refresh failed and `data` is older. */
    error: DocsFailure | null;
    /** When `data` arrived. */
    loadedAt: string | null;
    /** Read again now. */
    reload: () => void;
}

const EVERY = 60_000;
const RETRY = 15_000;
/** While a folder waits for its first read: the sample store settles in seconds, the server within a sync. */
const PENDING = isDocsLive ? 15_000 : 5_000;

interface Inner<T> {
    key: string | null;
    status: DocsLoadStatus;
    data: T | null;
    error: DocsFailure | null;
    loadedAt: string | null;
}

const fresh = <T,>(key: string | null): Inner<T> => ({ key, status: "loading", data: null, error: null, loadedAt: null });

/**
 * The shared read loop. `key` names what is read (null reads nothing and
 * stays "loading"); a new key starts over at "loading". `every` picks the
 * poll interval from what is on screen. Exported for the Phase 2 write
 * screens' own reads (the share check, the emails sent).
 */
export function useDocsLoad<T>(key: string | null, fetcher: () => Promise<DocsResult<T>>, every: (s: Inner<T>) => number = () => EVERY): DocsLoad<T> {
    const [state, setState] = useState<Inner<T>>(() => fresh<T>(key));
    const current = state.key === key ? state : fresh<T>(key);
    if (state.key !== key) setState(current);

    const keyRef = useRef(key);
    keyRef.current = key;
    const fetchRef = useRef(fetcher);
    fetchRef.current = fetcher;
    const seq = useRef(0);
    const alive = useRef(true);
    useEffect(() => {
        alive.current = true;
        return () => {
            alive.current = false;
        };
    }, []);

    const load = useCallback(() => {
        const k = keyRef.current;
        if (k === null) return;
        const mine = ++seq.current;
        void fetchRef.current().then((r) => {
            if (!alive.current || mine !== seq.current || keyRef.current !== k) return;
            setState((prev) => {
                if (r.ok) return { key: k, status: "ready", data: r.data, error: null, loadedAt: new Date().toISOString() };
                if (prev.key === k && prev.data !== null) return { ...prev, error: r };
                return { key: k, status: "error", data: null, error: r, loadedAt: null };
            });
        });
    }, []);

    useVisiblePoll(load, every(current), [key]);
    useEffect(() => subscribeDocs(load), [load]);

    return { status: current.status, data: current.data, error: current.error, loadedAt: current.loadedAt, reload: load };
}

/** A failed first read retries sooner, except a route that is not served yet: that answer does not change in 15 seconds. */
const retryOr = <T,>(s: Inner<T>, ms: number) => (s.status === "error" && s.error?.reason !== "notServed" ? RETRY : ms);

/** The documents board for one client (route 7). */
export function useBusinessDocuments(businessId: string | null): DocsLoad<BusinessDocuments> {
    return useDocsLoad<BusinessDocuments>(
        businessId ? `board:${businessId}` : null,
        () => listBusinessDocuments(businessId ?? ""),
        (s) => retryOr(s, s.data?.folder && !s.data.folder.lastReadAt
            ? PENDING
            // a doc a skill is writing: read again soon, so its row says when the draft is in (#4391)
            : s.data && boardRunGoing(s.data.documents ?? [], Date.now()) ? BOARD_RUN_POLL_MS : EVERY),
    );
}

/** One doc with its threads, for the drawer (route 8). Pass null while no doc is open. */
export function useDocument(documentId: string | null): DocsLoad<DocumentDetail> {
    return useDocsLoad<DocumentDetail>(documentId ? `doc:${documentId}` : null, () => getDocument(documentId ?? ""), (s) => retryOr(s, EVERY));
}

/** All clients (route 11). */
export function useDocsAcrossClients(): DocsLoad<DocsAcrossClients> {
    return useDocsLoad<DocsAcrossClients>("across", getDocsAcrossClients, (s) => retryOr(s, EVERY));
}

/** The Drive connection as oura@ (route 1). */
export function useDocsConnection(): DocsLoad<DocsConnection> {
    return useDocsLoad<DocsConnection>("connection", getDocsConnection, (s) => retryOr(s, EVERY));
}

/** The client's folder (route 3). `data` null with status "ready" means no folder is linked. */
export function useDocsFolder(businessId: string | null): DocsLoad<DocsFolder | null> {
    return useDocsLoad<DocsFolder | null>(
        businessId ? `folder:${businessId}` : null,
        () => getDocsFolder(businessId ?? ""),
        (s) => retryOr(s, s.data && !s.data.lastReadAt ? PENDING : EVERY),
    );
}

/**
 * The client's change requests, newest first (route 15), as a result that
 * can say "cannot be read yet" (`error.reason === "notServed"`). Call
 * `reload` after a reply or an update.
 */
export function useChangeRequests(businessId: string | null): DocsLoad<ChangeRequest[]> {
    return useDocsLoad<ChangeRequest[]>(businessId ? `requests:${businessId}` : null, () => readChangeRequests(businessId ?? ""), (s) => retryOr(s, EVERY));
}
